<template>
  <DashboardLayout>
    <template slot="navbarFilters">
      <div v-if="sellerOptions.length > 1 || marketplaceOptions.length > 1" class="navbar-filters fee-claims-filters">
        <select v-if="sellerOptions.length > 1" v-model="sellerId" class="fee-claims-filter" @change="load">
          <option value="">S.Accounts: All</option>
          <option v-for="s in sellerOptions" :key="s.id" :value="String(s.id)">{{ s.name }}</option>
        </select>
        <select v-if="marketplaceOptions.length > 1" v-model="marketplace" class="fee-claims-filter" @change="load">
          <option value="">Marketplaces: All</option>
          <option v-for="m in marketplaceOptions" :key="m" :value="m">Amazon.{{ mpSuffix(m) }}</option>
        </select>
      </div>
    </template>

    <div class="fee-claims">
      <div class="fee-claims-content">
        <h1 class="fee-claims-title">Fee Claims</h1>
        <p class="fee-claims-subtitle">When Amazon re-measures a product, you may be owed money on past orders, or paying too much on new ones.</p>

        <div v-if="notAvailable" class="fee-claims-empty">Fee Claims isn't switched on for this account.</div>
        <template v-else>
          <!-- KPIs (sums of the rows below, per currency: FeeClaimsController#kpis byCurrency) -->
          <div v-if="showKpis" class="fee-claims-kpis">
            <div class="fee-claims-kpi">
              <div class="k">Ready to claim</div>
              <div class="v green" :class="{ multi: kpiAmounts('readyToClaimCents').length > 1 }"><span v-for="(a, i) in kpiAmounts('readyToClaimCents')" :key="i">{{ a }}</span></div>
              <div class="s">{{ plural(kpis.readyToClaimOrders, 'order') }} · {{ plural(kpis.readyToClaimProducts, 'product') }}</div>
            </div>
            <div class="fee-claims-kpi">
              <div class="k">Claim within 7 days</div>
              <div class="v" :class="{ red: kpis.claimWithin7DaysCents > 0, multi: kpiAmounts('claimWithin7DaysCents').length > 1 }"><span v-for="(a, i) in kpiAmounts('claimWithin7DaysCents')" :key="i">{{ a }}</span></div>
              <div class="s">oldest orders leave Amazon's 90 days</div>
            </div>
            <div class="fee-claims-kpi">
              <div class="k">Filed, waiting</div>
              <div class="v" :class="{ multi: kpiAmounts('filedCents').length > 1 }"><span v-for="(a, i) in kpiAmounts('filedCents')" :key="i">{{ a }}</span></div>
              <div class="s">{{ plural(kpis.filedCount, 'claim') }} open</div>
            </div>
            <div class="fee-claims-kpi">
              <div class="k">Recovered</div>
              <div class="v green" :class="{ multi: kpiAmounts('recoveredCents').length > 1 }"><span v-for="(a, i) in kpiAmounts('recoveredCents')" :key="i">{{ a }}</span></div>
              <div class="s">since you started</div>
            </div>
          </div>

          <div class="fee-claims-sumbar">
            <button
              type="button"
              class="fee-claims-btn green"
              :disabled="!counts.to_claim || downloadingAll"
              @click="downloadAll"
            >
              <i :class="downloadingAll ? 'notched circle loading icon' : 'download icon'"></i>
              Download all claimable orders (XLSX)
            </button>
            <span class="fee-claims-muted">Every product in one file: Summary, Orders to claim, How to file.</span>
            <button type="button" class="fee-claims-howlink" @click="howOpen = !howOpen">How to claim</button>
          </div>
          <div v-if="downloadError" class="fee-claims-error">{{ downloadError }}</div>
          <div v-if="howOpen" class="fee-claims-how">
            <ol>
              <li>Download the order list for the product.</li>
              <li>In Seller Central, open a Seller Support case for FBA fees and attach the file.</li>
              <li>When Amazon refunds it, mark it here as Refunded.</li>
            </ol>
            <div class="fee-claims-muted">Amazon only looks at orders from the last 90 days, so claim the oldest first.</div>
          </div>

          <div class="fee-claims-seg">
            <button
              v-for="t in tabs"
              :key="t.key"
              type="button"
              :class="{ on: filter === t.key }"
              @click="setFilter(t.key)"
            >{{ t.label }} ({{ counts[t.key] || 0 }})</button>
          </div>

          <div class="fee-claims-table">
            <div class="fee-claims-head">
              <div>Product</div>
              <div>What Amazon changed</div>
              <div>{{ isFeeUp ? 'New fee' : 'Orders' }}</div>
              <div>{{ isFeeUp ? 'Extra / unit' : 'Amount' }}</div>
              <div>{{ filter === 'to_claim' ? 'Claim by' : '' }}</div>
              <div>Status</div>
            </div>

            <div v-if="loading && !claims.length" class="fee-claims-empty"><i class="notched circle loading icon"></i> Loading…</div>
            <div v-else-if="loadError" class="fee-claims-empty">{{ loadError }}</div>
            <div v-else-if="!claims.length" class="fee-claims-empty">Nothing here.</div>

            <div v-for="c in claims" :key="c.id" class="fee-claims-row">
              <div class="prod">
                <img v-if="c.imageUrl" :src="c.imageUrl" class="img" alt="" loading="lazy" />
                <div v-else class="img placeholder"><i class="image icon"></i></div>
                <div>
                  <div class="t">{{ c.title }}</div>
                  <div class="mono">{{ c.sellerSku }} · {{ c.asin }} · {{ c.marketplace }}</div>
                </div>
              </div>

              <div class="what">
                <div v-for="(e, i) in c.evidence" :key="i">{{ measureLabel(e.measure) }}: {{ e.text }}</div>
                <div v-if="!c.evidence || !c.evidence.length">Re-measured by Amazon</div>
                <div v-if="c.matchesListing" class="good">Now matches your listing</div>
                <div v-if="c.reversesChangeOn" class="small">Reverses the increase on {{ shortDate(c.reversesChangeOn) }}</div>
                <div class="small">{{ shortDate(c.feeChangedOn) }} · {{ money(c.previousFeeCents, c.currency) }} → {{ money(c.currentFeeCents, c.currency) }}</div>
              </div>

              <div class="num">
                <template v-if="isUp(c)">{{ money(c.currentFeeCents, c.currency) }}</template>
                <template v-else-if="c.orderListStatus === 'pending'"><span class="fee-claims-muted">Checking…</span></template>
                <template v-else>{{ c.orders != null ? c.orders : '—' }}</template>
              </div>

              <div class="amt" :class="isUp(c) ? 'amber' : 'green'">
                <template v-if="isUp(c)">+{{ money(c.currentFeeCents - c.previousFeeCents, c.currency) }}</template>
                <template v-else-if="c.claimCents != null">{{ money(c.claimCents, c.currency) }}</template>
                <template v-else>—</template>
              </div>

              <div class="when">
                <div v-if="filter === 'to_claim' && c.claimBy" :class="{ soon: c.claimByDueSoon }">{{ shortDate(c.claimBy) }}</div>
                <a
                  v-if="c.downloadUrl && !isUp(c) && filter !== 'done'"
                  :href="c.downloadUrl"
                  class="fee-claims-btn green small"
                  target="_blank"
                  rel="noopener noreferrer"
                >Download orders</a>
                <button v-if="isUp(c) && c.productId" type="button" class="fee-claims-btn small" @click="viewProduct(c)">View product</button>
              </div>

              <div>
                <select
                  class="fee-claims-status"
                  :value="c.status"
                  :disabled="savingId === c.id"
                  @change="setStatus(c, $event.target.value)"
                >
                  <option v-for="s in c.statuses" :key="s" :value="s">{{ statusLabel(s) }}</option>
                </select>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </DashboardLayout>
</template>

<script>
import DashboardLayout from './components/buybox/DashboardLayout.vue'
import { getCurrencySymbol } from './utils/currency.js'
import { buildProductDetailsQuery } from './utils/listViewUrlSync.js'

var API = '/api/v1/fee-claims'
var TABS = [
  { key: 'to_claim', label: 'To claim' },
  { key: 'fee_up', label: 'Fee went up' },
  { key: 'filed', label: 'Filed' },
  { key: 'done', label: 'Done' }
]
var STATUS_LABELS = {
  to_claim: 'To claim',
  filed: 'Filed',
  refunded: 'Refunded',
  declined: 'Declined',
  to_check: 'To check',
  remeasure_requested: 'Asked Amazon to re-measure',
  corrected: 'Amazon corrected it',
  measurement_right: 'Measurement is right'
}
var MEASURE_LABELS = { size_tier: 'Size tier', dimensions: 'Dimensions', weight: 'Weight' }
var MP_SUFFIX = { US: 'com', CA: 'ca', MX: 'com.mx', UK: 'co.uk', GB: 'co.uk', DE: 'de', FR: 'fr', IT: 'it', ES: 'es', JP: 'co.jp', AU: 'com.au' }
var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// Fee Claims page (tenants with feeClaims): KPIs, one table with four filters, status per row, per-row and
// all-in-one order list downloads. Rows, KPIs and counts come from GET /api/v1/fee-claims.
export default {
  name: 'FeeClaimsPage',
  components: { DashboardLayout },
  data: function () {
    return {
      tabs: TABS,
      // KPI cards hidden for now; the API still returns kpis (FeeClaimsController#kpis). Set true to show them.
      showKpis: false,
      filter: 'to_claim',
      sellerId: '',
      marketplace: '',
      claims: [],
      kpis: {},
      counts: {},
      sellerOptions: [],
      marketplaceOptions: [],
      loading: false,
      loadError: '',
      notAvailable: false,
      savingId: null,
      downloadingAll: false,
      downloadError: '',
      howOpen: false
    }
  },
  computed: {
    isFeeUp: function () { return this.filter === 'fee_up' }
  },
  mounted: function () {
    var q = this.$route.query || {}
    if (TABS.some(function (t) { return t.key === q.filter })) this.filter = q.filter
    this.load()
  },
  methods: {
    params: function () {
      var p = { filter: this.filter }
      if (this.sellerId) p.seller_ids = this.sellerId
      if (this.marketplace) p.marketplaces = this.marketplace
      return p
    },
    load: function () {
      var self = this
      var gen = (this._gen = (this._gen || 0) + 1)
      this.loading = true
      this.loadError = ''
      return this.$http.get(API, { params: this.params() }).then(function (res) {
        if (gen !== self._gen) return
        var body = res.body || {}
        self.claims = body.claims || []
        self.kpis = body.kpis || {}
        self.counts = body.counts || {}
        var f = body.filters || {}
        self.sellerOptions = f.sellers || []
        self.marketplaceOptions = f.marketplaces || []
        self.$root.feeClaimsToClaimCount = body.toClaimCount != null ? body.toClaimCount : self.$root.feeClaimsToClaimCount
        self.loading = false
      }, function (res) {
        if (gen !== self._gen) return
        self.loading = false
        if (res && res.status === 404) {
          self.notAvailable = true
        } else {
          self.loadError = "Couldn't load fee claims. Please try again."
        }
      })
    },
    setFilter: function (key) {
      if (this.filter === key) return
      this.filter = key
      this.claims = []
      this.$router.replace({ path: this.$route.path, query: Object.assign({}, this.$route.query, { filter: key }) })
      this.load()
    },
    setStatus: function (claim, status) {
      var self = this
      if (status === claim.status) return
      this.savingId = claim.id
      this.$http.patch(API + '/' + claim.id, { status: status }).then(function () {
        self.savingId = null
        self.load()
        if (self.$root.loadFeeClaimsCount) self.$root.loadFeeClaimsCount(true)
      }, function (res) {
        self.savingId = null
        var msg = res && res.body && res.body.message
        window.alert(msg || "Couldn't update the status. Please try again.")
        self.load()
      })
    },
    // Same XHR + blob pattern as the costs CSV template: the session cookie authenticates it.
    downloadAll: function () {
      var self = this
      var p = this.params()
      delete p.filter
      var qs = Object.keys(p).map(function (k) { return encodeURIComponent(k) + '=' + encodeURIComponent(p[k]) }).join('&')
      var xhr = new XMLHttpRequest()
      xhr.open('GET', API + '/download' + (qs ? '?' + qs : ''), true)
      xhr.responseType = 'blob'
      this.downloadingAll = true
      this.downloadError = ''
      xhr.onload = function () {
        self.downloadingAll = false
        if (xhr.status !== 200) {
          self.downloadError = xhr.status === 404 ? 'Nothing to claim right now.' : "Couldn't build the file. Please try again."
          return
        }
        var match = (xhr.getResponseHeader('Content-Disposition') || '').match(/filename="?([^";]+)"?/)
        var url = window.URL.createObjectURL(xhr.response)
        var link = document.createElement('a')
        link.href = url
        link.download = match ? match[1] : 'SentryKit - FBA Fee Claims.xlsx'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }
      xhr.onerror = function () {
        self.downloadingAll = false
        self.downloadError = "Couldn't build the file. Please try again."
      }
      xhr.send()
    },
    viewProduct: function (claim) {
      this.$router.push({ path: '/product_details', query: buildProductDetailsQuery(String(claim.productId), this.$route.fullPath) })
    },
    isUp: function (claim) { return claim.reason === 'remeasured_up' },
    statusLabel: function (s) { return STATUS_LABELS[s] || s },
    measureLabel: function (m) { return MEASURE_LABELS[m] || m },
    mpSuffix: function (m) { return MP_SUFFIX[m] || String(m).toLowerCase() },
    plural: function (n, word) {
      var v = n || 0
      return v + ' ' + word + (v === 1 ? '' : 's')
    },
    // One KPI in each marketplace's own currency ("$41.86", "CA$12.00"), never added up across currencies.
    // Currencies with nothing in this KPI are left out; all zero shows the first one's 0.
    kpiAmounts: function (key) {
      var self = this
      var list = this.kpis.byCurrency || []
      if (list.length <= 1) {
        return [this.money(this.kpis[key], this.kpis.currency || (list[0] && list[0].currency) || 'USD')]
      }
      var shown = list.filter(function (c) { return c[key] > 0 })
      if (!shown.length) shown = [list[0]]
      return shown.map(function (c) { return self.money(c[key], c.currency) })
    },
    // "$1.47", "CA$1.47" (currency symbols from utils/currency.js).
    money: function (cents, currency) {
      var amount = Math.abs((cents || 0) / 100).toFixed(2)
      var sign = cents < 0 ? '-' : ''
      return currency === null ? sign + amount : sign + getCurrencySymbol(currency || 'USD') + amount
    },
    shortDate: function (iso) {
      if (!iso) return ''
      var parts = String(iso).split('-')
      if (parts.length < 3) return iso
      return MONTHS[parseInt(parts[1], 10) - 1] + ' ' + parseInt(parts[2], 10)
    }
  }
}
</script>

<style scoped>
.fee-claims { min-height: 100vh; background: #f8fafc; color: #1e293b; }
.fee-claims-content { padding: 32px; max-width: 1180px; }
.fee-claims-title { font-size: 22px; font-weight: 700; margin: 0 0 4px; }
.fee-claims-subtitle { font-size: 13px; color: #64748b; margin: 0; }
.fee-claims-muted { color: #64748b; font-size: 12.5px; }
.fee-claims-filters { display: flex; gap: 8px; }
.fee-claims-filter { border: 1px solid #e2e8f0; border-radius: 7px; padding: 6px 10px; font-size: 12.5px; background: #fff; }

.fee-claims-kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-top: 18px; }
.fee-claims-kpi { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px 14px; }
.fee-claims-kpi .k { font-size: 11px; letter-spacing: .05em; color: #64748b; font-weight: 700; text-transform: uppercase; }
.fee-claims-kpi .v { font-size: 24px; font-weight: 900; margin-top: 2px; }
.fee-claims-kpi .v.multi { font-size: 18px; line-height: 1.3; }
.fee-claims-kpi .v.multi span { display: block; }
.fee-claims-kpi .s { font-size: 12px; color: #64748b; }
.green { color: #15803d; }
.red { color: #dc2626; }
.amber { color: #d97706; }

.fee-claims-sumbar { display: flex; align-items: center; gap: 12px; margin-top: 14px; flex-wrap: wrap; }
.fee-claims-btn { border: 1px solid #e2e8f0; background: #fff; border-radius: 6px; padding: 7px 12px; font-size: 12.5px; color: #1e293b; cursor: pointer; white-space: nowrap; display: inline-block; text-decoration: none; }
.fee-claims-btn.green { background: #16a34a; border-color: #16a34a; color: #fff; font-weight: 700; }
.fee-claims-btn.small { padding: 5px 10px; margin-top: 6px; }
.fee-claims-btn:disabled { opacity: .5; cursor: default; }
.fee-claims-btn .icon { margin: 0 4px 0 0; }
.fee-claims-howlink { font-size: 13px; color: #1b4b82; font-weight: 700; background: none; border: 0; text-decoration: underline; cursor: pointer; }
.fee-claims-how { margin-top: 10px; background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px 16px; font-size: 13px; line-height: 1.6; max-width: 640px; }
.fee-claims-how ol { padding-left: 18px; margin: 0 0 6px; }
.fee-claims-error { margin-top: 8px; color: #dc2626; font-size: 12.5px; }

.fee-claims-seg { display: flex; gap: 6px; margin-top: 16px; flex-wrap: wrap; }
.fee-claims-seg button { border: 1px solid #e2e8f0; background: #fff; border-radius: 18px; padding: 6px 13px; font-size: 12.5px; color: #1e293b; cursor: pointer; }
.fee-claims-seg button.on { background: #1b4b82; border-color: #1b4b82; color: #fff; font-weight: 700; }

.fee-claims-table { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; margin-top: 12px; }
.fee-claims-head, .fee-claims-row { display: grid; grid-template-columns: minmax(250px, 1fr) 230px 90px 100px 150px 190px; gap: 12px; align-items: center; }
.fee-claims-head { padding: 10px 16px; font-size: 11px; letter-spacing: .05em; color: #64748b; font-weight: 700; text-transform: uppercase; }
.fee-claims-row { padding: 13px 16px; border-top: 1px solid #e2e8f0; }
.fee-claims-row .prod { display: flex; gap: 12px; align-items: center; min-width: 0; }
.fee-claims-row .img { width: 38px; height: 38px; border-radius: 6px; object-fit: contain; background: #eef2f7; flex: none; }
.fee-claims-row .img.placeholder { display: flex; align-items: center; justify-content: center; color: #94a3b8; }
.fee-claims-row .t { font-size: 13px; line-height: 1.35; }
.fee-claims-row .mono { font-family: ui-monospace, Menlo, monospace; font-size: 12px; color: #64748b; margin-top: 2px; }
.fee-claims-row .what { font-size: 12.5px; line-height: 1.45; }
.fee-claims-row .what .good { color: #15803d; font-weight: 700; }
.fee-claims-row .small { font-size: 12px; color: #64748b; margin-top: 2px; }
.fee-claims-row .num { font-size: 13px; }
.fee-claims-row .amt { font-weight: 900; font-size: 15px; }
.fee-claims-row .when { font-size: 13px; }
.fee-claims-row .soon { color: #dc2626; font-weight: 700; }
.fee-claims-status { border: 1px solid #e2e8f0; border-radius: 6px; padding: 6px; font-size: 12.5px; width: 100%; background: #fff; }
.fee-claims-empty { padding: 28px; text-align: center; color: #64748b; }

@media (max-width: 1100px) {
  .fee-claims-kpis { grid-template-columns: repeat(2, 1fr); }
  .fee-claims-head { display: none; }
  .fee-claims-row { grid-template-columns: 1fr 1fr; }
}
</style>



// WEBPACK FOOTER //
// FeeClaimsPage.vue?3fdb0316